import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Link,
  createFileRoute,
  redirect,
  useNavigate,
} from "@tanstack/react-router";
import { ArrowRight, Lock, Sparkles } from "lucide-react";
import { motion } from "motion/react";

import { CoachCard, type CoachCardMinimalData } from "~/components/coach-card";
import { Button } from "~/components/ui/button";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { setPostAuthCookies } from "~/lib/shared/post-auth-routing";

const VISIBLE_COUNT = 3;

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/_public/discovery/results")({
  beforeLoad: ({ context }) => {
    // If no match results in cache, redirect to discovery landing
    const cached = context.queryClient.getQueryData<CoachCardMinimalData[]>([
      "quickMatchResults",
    ]);
    if (!cached || cached.length === 0) {
      throw redirect({ to: "/discovery" });
    }
  },
  head: () =>
    createNoIndexMeta(
      "Your Coach Matches — Dream Coach Match",
      "View your personalized coach matches based on your goals.",
    ),
  component: QuickMatchResults,
});

// ---------------------------------------------------------------------------
// Animations
// ---------------------------------------------------------------------------

const STAGGER = {
  hidden: {},
  show: { transition: { staggerChildren: 0.06 } },
} as const;

const CARD_VARIANT = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
} as const;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/** Discovery is a known-intent entry point: visitors here are client-intent by construction (R28). */
function captureClientIntent(): void {
  setPostAuthCookies({ intent: "client", destination: "/coach-finder" });
}

function QuickMatchResults() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: matches = [] } = useQuery<CoachCardMinimalData[]>({
    queryKey: ["quickMatchResults"],
    // Data is seeded from cache by the context step — no fetch needed
    queryFn: () =>
      queryClient.getQueryData<CoachCardMinimalData[]>(["quickMatchResults"]) ??
      [],
    staleTime: Infinity,
  });

  if (matches.length === 0) {
    navigate({ to: "/discovery" });
    return null;
  }

  const visible = matches.slice(0, VISIBLE_COUNT);
  const blurred = matches.slice(VISIBLE_COUNT);

  return (
    <div className="mx-auto max-w-5xl px-4 py-12 sm:py-16">
      {/* Header */}
      <div className="text-center">
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-3 inline-flex items-center gap-2 rounded-full border border-purple-100 bg-purple-50 px-4 py-1.5 text-xs font-medium text-purple-700"
        >
          <Sparkles className="size-3.5" />
          {matches.length}+ coaches matched
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="font-heading text-3xl font-semibold tracking-tight sm:text-4xl"
        >
          Your top coach matches
        </motion.h1>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mt-3 text-base text-gray-500"
        >
          Based on your goals and context. Click a coach to view their full
          profile.
        </motion.p>
      </div>

      {/* Visible matches */}
      <motion.div
        className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
        variants={STAGGER}
        initial="hidden"
        animate="show"
      >
        {visible.map((match) => (
          <motion.div key={match.coach.id} variants={CARD_VARIANT}>
            <CoachCard
              coachData={match}
              href={`/${match.coach.username}`}
              source="discovery_results"
              isEnabled
            />
          </motion.div>
        ))}
      </motion.div>

      {/* Blurred matches + sign-up gate */}
      {blurred.length > 0 && (
        <div className="relative mt-5">
          <motion.div
            className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3"
            variants={STAGGER}
            initial="hidden"
            animate="show"
          >
            {blurred.map((match) => (
              <motion.div
                key={match.coach.id}
                variants={CARD_VARIANT}
                className="overflow-hidden rounded-2xl"
              >
                <CoachCard
                  coachData={match}
                  isEnabled={false}
                  onLockClick={() => {
                    captureClientIntent();
                    navigate({
                      to: "/login",
                      search: { redirect: "/coach-finder" },
                    });
                  }}
                />
              </motion.div>
            ))}
          </motion.div>

          {/* Gradient fade over blurred cards */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-linear-to-t from-white to-transparent" />
        </div>
      )}

      {/* Sign-up / refinement CTA */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="mt-10 overflow-hidden rounded-2xl border border-purple-200 bg-linear-to-br from-purple-50 to-violet-50 p-8 text-center sm:p-10"
      >
        <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-purple-100">
          <Lock className="size-5 text-purple-700" />
        </div>
        <h2 className="mt-4 text-xl font-semibold text-gray-900">
          {blurred.length > 0
            ? `+ ${blurred.length} more matches waiting`
            : "Want even better matches?"}
        </h2>
        <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">
          Create a free account to see all your matches, or answer a few more
          questions to refine your results.
        </p>
        <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
          <Link
            to="/login"
            search={{ redirect: "/coach-finder" }}
            onClick={captureClientIntent}
          >
            <Button size="lg" variant="primary" className="gap-2 px-8">
              Sign up to see all matches
              <ArrowRight className="size-4" />
            </Button>
          </Link>
          <Link to="/discovery/refine">
            <Button size="lg" className="gap-2">
              Refine my matches
            </Button>
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
